<template>
    <div
        class="content-wrapper position-relative"
    >
        <DebugContent>
            <v-container>
                <details>
                    <summary
                        class="cursor-pointer"
                    >
                        DEBUG: Dynamic Page from CMS
                    </summary>
                    <div
                        v-if="contentDisplayObj && !lo_isError(contentDisplayObj)"
                        class="my-10"
                    >
                        <h3>Meta</h3>
                        <ul
                            v-if="contentDisplayObj.meta"
                        >
                            <li
                                v-for="(v, k) in contentDisplayObj.meta"
                                :key="k"
                            >
                                <strong>{{ k }}:</strong>
                                {{ v }}
                            </li>
                        </ul>
                    </div>
                </details>
            </v-container>
        </DebugContent>

        <!-- Content / Error / Progress -->
        <template
            v-if="!currentError && contentDisplayObj && !lo_isError(contentDisplayObj) && contentDisplayObj.content && contentDisplayObj.meta && contentDisplayObj?.baseComponent"
        >
            <Suspense>
                <component
                    :is="contentComponents[contentDisplayObj.baseComponent]"
                    :page-key="pageKey"
                    :content-data="contentDisplayObj.content"
                    :content-meta="contentDisplayObj.meta"
                ></component>
            </Suspense>
        </template>
        <template
            v-else-if="currentError"
        >
            <ContentPageError :error="currentError"></ContentPageError>
        </template>
        <template
            v-else
        >
            <!-- Progress Indicator -->
            <div

                class="
                    d-flex
                    flex-column
                    align-center
                    justify-center
                    content-wrapper-progress
                    absolute-center
                    h-100
                "
            >
                <v-progress-circular
                    color="accent"
                    width="8"
                    size="62"
                    indeterminate
                ></v-progress-circular>
            </div>
        </template>
    </div>
</template>

<script lang="ts" setup>
    import DebugContent from '~/components/DebugContent/DebugContent.vue';
    import ContentPageError from '~/components/error/ContentPageError/ContentPageError.vue';
    
    import type { ContentComponentType, GetContentDataResponse } from '~/types';
    import type { NuxtError } from '#app';
    import { useContent } from '~/composables';
    import { isError as lo_isError } from 'es-toolkit';
    import type { WatchStopHandle } from 'vue';
    import { useServerSeoMeta } from '#imports';
    import { pathJoin, getPseudoHash } from '~/utils';

    definePageMeta({
        scrollToTop: true,
        path: '/content/:catchAll(.*)',
        alias: [
            '/:catchAll(.*)'
        ]
    });

    const contentComponents: Record<ContentComponentType, Nullable<ReturnType<typeof resolveComponent>>> = {
        TypePage: resolveComponent('ContentTypePage'),
        TypeCollection: resolveComponent('ContentTypeCollection'),
        TypeArticle: resolveComponent('ContentTypeArticle'),
        TypeProduct: resolveComponent('ContentTypeProduct'),
        None: null
    };

    const $config = useRuntimeConfig();
    const route = useRoute();
    const useContentObj = useContent();
    const brandTitle = $config.public.brand.title;
    const docTitle = ref();
    const docMeta = ref();

    useHead({
        title: docTitle,
        meta: docMeta
    });

    const pageKey = computed<string>(() => `keyContent_${getPseudoHash(route.fullPath)}`);

    const {
        data: contentDisplayObj,
        status: contentStatus,
        error: currentError
    } = await useLazyAsyncData<GetContentDataResponse | NuxtError | Error | void, NuxtError>(
        pageKey.value,
        async () => {
            const result = await useContentObj.getPageContent(route);

            if (lo_isError(result)) {
                throw result;
            }

            return result;
        }
    );

    let unWatchContentDisplayObj: WatchStopHandle;
    unWatchContentDisplayObj = watch(
        contentDisplayObj,
        async (newContentObj) => {
            // Only run once after loaded
            if (newContentObj) {
                if (!lo_isError(newContentObj) && newContentObj.meta.type === 'redirect' && newContentObj?.meta.path) {
                    const redirectPath = pathJoin('/', newContentObj.meta.path);
                    await navigateTo(redirectPath, { redirectCode: 301 });
                } else {
                    setPageSeo();
                }

                unWatchContentDisplayObj?.();
            }
        }, { immediate: true }
    );

    let unWatchError: WatchStopHandle;
    unWatchError = watch(
        currentError,
        (newError: Undefinable<NuxtError>) => {
            if (!newError) {
                return;
            }

            if (isApiDown(newError)) {
                // Fatal Error
                showError({ statusCode: 503, message: 'Site Offline', fatal: true });
            } else {
                const event = useRequestEvent();
                const errMsg = 'Page Not Found';
                docTitle.value = `${errMsg} | ${brandTitle}`;

                if (event) {
                    setResponseStatus(event, 404, errMsg);
                }
            }

            unWatchError?.();
        }, { immediate: true }
    );

    function setPageSeo() {
        if (!currentError.value && contentDisplayObj.value && 'meta' in contentDisplayObj.value && 'content' in contentDisplayObj.value) {
            const { meta } = contentDisplayObj.value;

            if (meta.type === 'redirect') {
                return;
            }

            const metaItems = [];
            const baseURL = $config.public.environment.homeUrl;


            const title = `${meta.seoTitle || meta.title} | ${brandTitle}`;
            docTitle.value = title;

            const serverMetaObj: Record<string, string> = {
                ogTitle: title,
                twitterTitle: title,
                ogUrl: `${baseURL}${route.fullPath}`
            };

            if (meta.seoDescription) {
                metaItems.push({
                    hid: 'description',
                    name: 'description',
                    content: meta.seoDescription
                });

                serverMetaObj.ogDescription = meta.seoDescription;
                serverMetaObj.twitterDescription = meta.seoDescription;
            }

            if (meta.seoKeywords) {
                metaItems.push({
                    hid: 'keywords',
                    name: 'keywords',
                    content: meta.seoKeywords
                });
            }

            docMeta.value = metaItems;

            useServerSeoMeta(serverMetaObj);
        }
    }

    function isApiDown(err: NuxtError | Error | void): boolean {
        return Boolean(err?.toString()?.includes('no response'));
    };

    onBeforeUnmount(() => {
        unWatchError?.();
        unWatchContentDisplayObj?.();
    });
</script>

<style lang="scss">
.content-wrapper {
    min-height: 60vh;
}
</style>
